import { Box, TabPanel, TabPanelProps, TabPanels } from '@chakra-ui/react'
import moment from 'moment'

import {
  DetailDrawerHeader,
  DetailDrawerList,
  DetailDrawerListMedia,
  DetailDrawerListPaper,
  DetailDrawerListPressRelease,
  DetailDrawerListTrial,
  DetailDrawerSummary,
  DetailDrawerTabs,
} from 'modules/DetailDrawer'

import TableView from 'routes/apps/TableView'
import { useAppRoute } from 'routes/utils'

import { SlideOver } from 'components'

import { IResponseBase } from 'api/types'

import { IModel } from 'interfaces/model.interface'
import { ViewData } from 'interfaces/navigationPage.interface'

import { apps } from 'config/apps'
import {
  InfluenzaCandidatePapersModel,
  InfluenzaTrialsModel,
} from 'config/apps/Influenza/Science360/VaccinesTherapeutics/InfluenzaModels'
import {
  RSVToolMetaVaccineEfficacyModel,
  RsvApprovalsVaccinesModel,
} from 'config/apps/RSV/Science360/VaccinesTherapeutics/RSVModels'
import {
  CandidateRankingData,
  MediaModel,
  PressReleasesModel,
} from 'config/common/baseModel'

import { DetailDrawerListApprovals } from './ApprovalsDetailView'

const StyledTabPanel: React.FC<React.PropsWithChildren<TabPanelProps>> = (
  props
) => <TabPanel height='100%' p={0} {...props} />

type PropTypes<D extends Record<CandidateRankingData, any>> = {
  handleClose: () => void
  candidate: IResponseBase<Extract<keyof D, string>>
  model: IModel<D>
  title: Extract<keyof D, string>
  viewData: ViewData
}

function Detail<D extends Record<CandidateRankingData, any>>(
  props: PropTypes<D>
) {
  const { handleClose, candidate, model, title, viewData } = props

  const app = useAppRoute()

  const exportName = (modelTarget: IModel<any>) =>
    `${apps[app].name}_${model.name}_${candidate && candidate[title]}_${
      modelTarget.name
    }`

  return (
    <SlideOver show={true} handleClose={handleClose}>
      <DetailDrawerHeader
        isLoading={false}
        title={candidate && candidate[title]}
        handleClose={handleClose}
      />
      <DetailDrawerTabs
        tabs={[
          'Summary',
          'Media',
          'Press Releases',
          'Papers',
          'Trials',
          'Results',
          'Approvals',
        ]}
      >
        <Box flexGrow={1} flexBasis={0} overflow='hidden'>
          <TabPanels height='100%'>
            <StyledTabPanel p={6} overflow='auto'>
              <DetailDrawerSummary rowData={candidate} model={model} />
            </StyledTabPanel>
            <StyledTabPanel>
              <DetailDrawerList
                id={1}
                viewData={viewData}
                type='Media'
                sortOrder={(x) => moment(x.datePublished)}
                model={MediaModel}
                ids={
                  candidate.resp360MediaVaccine ||
                  candidate.resp360MediaTreatment
                }
                itemComponent={DetailDrawerListMedia}
              />
            </StyledTabPanel>
            <StyledTabPanel>
              <DetailDrawerList
                id={2}
                viewData={viewData}
                type='PressReleases'
                sortOrder={(x) => moment(x.datePublished)}
                model={PressReleasesModel}
                ids={
                  candidate.resp360PressReleasesVaccine ||
                  candidate.resp360PressReleasesTreatment
                }
                itemComponent={DetailDrawerListPressRelease}
              />
            </StyledTabPanel>
            <StyledTabPanel>
              <DetailDrawerList
                id={3}
                viewData={viewData}
                type='Papers'
                model={InfluenzaCandidatePapersModel}
                sortOrder={(x) => moment(x.publicationDate)}
                ids={
                  candidate.resp360PublicationsVaccine ||
                  candidate.resp360PublicationsTreatment
                }
                itemComponent={DetailDrawerListPaper}
              />
            </StyledTabPanel>
            <StyledTabPanel>
              <DetailDrawerList
                id={4}
                viewData={viewData}
                sortOrder={(x) => moment(x.fStudyCompletion)}
                model={InfluenzaTrialsModel}
                ids={candidate.clinicalTrialsIds}
                itemComponent={DetailDrawerListTrial}
              />
            </StyledTabPanel>
            <StyledTabPanel>
              <TableView
                id={5}
                viewData={viewData}
                model={RSVToolMetaVaccineEfficacyModel}
                ids={
                  candidate.resp360VaccineEfficacy ||
                  candidate.resp360TreatmentEfficacy
                }
                exportName={exportName(RSVToolMetaVaccineEfficacyModel)}
              />
            </StyledTabPanel>
            <StyledTabPanel>
              <DetailDrawerList
                id={6}
                viewData={viewData}
                model={RsvApprovalsVaccinesModel}
                sortOrder={(x) => moment(x.approvalDate)}
                ids={
                  candidate.resp360ApprovalsVaccine ||
                  candidate.resp360ApprovalsTreatment
                }
                itemComponent={DetailDrawerListApprovals}
              />
            </StyledTabPanel>
          </TabPanels>
        </Box>
      </DetailDrawerTabs>
    </SlideOver>
  )
}

export default Detail
